import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Dayjs } from 'dayjs';
import _get from 'lodash/get';
import { Values } from '@lib/interfaces/form';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import DateRangePicker from '@lib/components/DateTimePicker/DateRangePicker';
import { InputSizes, InputTypes } from '@lib/components/Input/enums';
import TasksFilterFieldNames from '@lib/enums/fieldNames/tasksFilterFieldNames';
import Modal from '@lib/components/Modal/Modal';
import Form, { FormRef } from '@lib/components/ReactHookForm/FormContainer';
import TasksTimelineFilterActions from 'routes/Dashboard/TasksTimeline/TasksTimelineFilterActions';
import formStyles from '@lib/assets/styles/Form.module.scss';
import { Modals } from '@lib/enums/modals';
import useModalManagement from '@lib/hooks/useModalManagement';
import useFilterFields, {
  TASKS_OVERVIEW_FILTER_EMPTY_VALUES,
} from 'routes/Dashboard/TasksOverview/utils/useFilterFields';
import { SelectOption } from '@lib/components/Select/types';
import useQueries from './utils/useQueries';
import styles from './TasksOverviewFilter.module.scss';

const filterModal = Modals.DashboardOverviewFilterModal;
const modals = [filterModal];

type Props = {
  resetPeriodFilterParams: () => void;
  resetFilterParams: () => void;
  onUpdateFilterParams: (values: Values) => void;
  filterParams: Values;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  basePeriodStartParam: string;
  basePeriodEndParam: string;
};

export default function TasksOverviewFilter(props: Props) {
  const {
    resetFilterParams,
    filterParams,
    resetPeriodFilterParams,
    onUpdateFilterParams,
    minDate,
    maxDate,
    basePeriodStartParam,
    basePeriodEndParam,
  } = props;
  const { t } = useTranslation();
  const formRef = useRef<FormRef>(undefined);
  const dateRangePickerRef = useRef(undefined);
  const { fetchSiteAreasSearchListLazyQuery, getSitesLoadOptions } =
    useQueries();

  const useModalManagementResult = useModalManagement(modals);
  const { isModalOpened, openModal, closeModal } = useModalManagementResult;
  const openFilter = () => openModal(filterModal);
  const closeFilter = () => closeModal(filterModal);
  const clearFilter = () => {
    closeModal(filterModal);
    resetFilterParams();
  };

  const filterCounter =
    (
      _get(filterParams, TasksFilterFieldNames.Filter, []) as {
        site: SelectOption;
      }[]
    ).filter(({ site }) => !!site?.value)?.length || 0;

  const periodValue = (filterParams[
    TasksFilterFieldNames.Period
  ] as string[]) || [basePeriodStartParam, basePeriodEndParam];

  const onTimeFilterChange = (v: string[]) => {
    if (Array.isArray(v) && v[0] && v[1]) {
      formRef.current?.setValue(TasksFilterFieldNames.Period, v);
      onUpdateFilterParams({
        ...filterParams,
        [TasksFilterFieldNames.Period]: v,
      });
    } else {
      resetPeriodFilterParams();
    }
  };

  const defaultValues =
    filterParams && Object.keys(filterParams).length > 0
      ? filterParams
      : TASKS_OVERVIEW_FILTER_EMPTY_VALUES;

  const fields = useFilterFields({
    getSitesLoadOptions,
    fetchSiteAreasSearchListLazyQuery,
    closeFilter,
  });

  const onSubmit = (values: Values) => {
    onUpdateFilterParams(values);
    closeFilter();
  };

  return (
    <>
      {' '}
      <PageBody className={styles.filterWrap}>
        <WhiteBox
          className={styles.filter}
          verticalPaddings="extraSmall"
          horizontalPaddings="small"
        >
          <div className={styles.filterRow}>
            <div>
              <Chip
                counter={filterCounter}
                label={t('filter')}
                leftIcon="filter_alt"
                leftIconSymbolsOutlined
                onClick={openFilter}
                size={ChipSizes.large}
                type={ChipTypes.chipFilter}
                tooltipForCroppedTextDisabled
              />
            </div>
            {!!filterCounter && (
              <div>
                <Button
                  buttonSize={ButtonSizes.large}
                  buttonText={t('reset')}
                  buttonType={ButtonTypes.primaryText}
                  onClick={clearFilter}
                />
              </div>
            )}
          </div>
          <div className={styles.dateRangePickerCol}>
            <DateRangePicker
              label={t('period')}
              onChange={onTimeFilterChange}
              ref={dateRangePickerRef}
              size={InputSizes.medium}
              type={InputTypes.dateRange}
              value={periodValue}
              minDate={minDate}
              maxDate={maxDate}
            />
          </div>
        </WhiteBox>
      </PageBody>
      <Modal
        isOpen={isModalOpened(filterModal)}
        caption={t('filter')}
        onClose={closeFilter}
      >
        <Form
          formRef={formRef}
          fields={fields}
          onSubmit={onSubmit}
          defaultValues={defaultValues}
          actionsComponent={TasksTimelineFilterActions}
          actionsComponentProps={{
            onCancel: closeFilter,
          }}
          formClassName={formStyles.form}
        />
      </Modal>
    </>
  );
}
